import { inject, observer } from "mobx-react";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import Loading from "../../../components/Loading";
import ExternalUserManagementStore from "../../../modules/Admin/ExternalUserManagement/store";
import {
  SSOExternalUserGridListDto,
  SSOPermissionDto,
} from "../../../modules/Admin/ExternalUserManagement/types";
import { MessageResultState } from "../../../types/general-types";
import { REDESIGN_NS } from "../../i18n";
import { getPermissionKeys } from "../../lib/permissionKeys";
import Combobox from "../../ui/Combobox";
import { IconPlus, IconTrash } from "../../ui/icons";
import { Modal, ModalFooter } from "../../ui/Modal";

interface Props {
  ExternalUserManagementStore?: ExternalUserManagementStore;
  isOpen: boolean;
  onClose: () => void;
  user: SSOExternalUserGridListDto;
}

/**
 * `create-permisson` bir full replace'tir: kullanıcının tüm yetki satırlarını
 * silip listeyi yeniden yazar, bu yüzden rol kaldırmak ayrı çağrı istemez.
 */
const RoleAssignmentModal: FC<Props> = inject("ExternalUserManagementStore")(
  observer(
    ({ ExternalUserManagementStore: store, isOpen, onClose, user }: Props) => {
      const { t } = useTranslation(REDESIGN_NS);

      const [permissions, setPermissions] = useState<SSOPermissionDto[]>([]);
      const [draft, setDraft] = useState("");
      const [isBusy, setIsBusy] = useState(false);
      // `isLoaded` yalnızca liste GERÇEKTEN geldiğinde true olur; kaydetmeyi açan kapı budur.
      const [isLoading, setIsLoading] = useState(true);
      const [isLoaded, setIsLoaded] = useState(false);
      const [keys, setKeys] = useState<string[]>([]);
      const [areKeysLoaded, setAreKeysLoaded] = useState(false);

      useEffect(() => {
        let isMounted = true;
        getPermissionKeys().then((result) => {
          if (!isMounted) return;
          setKeys(result);
          setAreKeysLoaded(true);
        });
        return () => {
          isMounted = false;
        };
      }, []);

      useEffect(() => {
        setIsLoaded(false);
        setIsLoading(true);
        store
          ?.GetUserPermissions(user.id)
          .then((response) => {
            if (response.state === MessageResultState.SUCCESS) {
              setPermissions(response.result.permissionsList || []);
              setIsLoaded(true);
            }
          })
          // Hata toast'ını interceptor basıyor; `isLoaded` false kalır, kaydetmek yasak.
          .catch(() => undefined)
          .then(() => setIsLoading(false));
      }, [store, user.id]);

      const add = () => {
        const key = draft.trim();
        if (!key) return;
        if (permissions.some((item) => item.permissionKey === key)) {
          toast.error(t("The same permission cannot be given to the user again!"));
          return;
        }
        setPermissions([...permissions, { permissionKey: key, sSOPermissionId: 0 }]);
        setDraft("");
      };

      const remove = (key: string | undefined) =>
        setPermissions(permissions.filter((item) => item.permissionKey !== key));

      // Yükleme bitmeden kaydetmek boş liste yollar ve full replace tüm yetkileri siler.
      const submit = async () => {
        if (!store || !isLoaded) return;
        setIsBusy(true);
        try {
          const response = await store.createPermission({
            sSOExternalUserId: user.id,
            permissionsList: permissions,
          });
          if (response.state === MessageResultState.SUCCESS) onClose();
        } finally {
          setIsBusy(false);
        }
      };

      return (
        <Modal
          isOpen={isOpen}
          onClose={onClose}
          width={560}
          title={t("{{name}} - Role Assignment", { name: user.nameSurname })}
          footer={
            <ModalFooter
              onClose={onClose}
              onSubmit={submit}
              submitLabel={t("Save")}
              isBusy={isBusy || !isLoaded}
            />
          }
        >
          <div className="sso-rd__form-grid sso-rd__form-grid--single">
            <div className="sso-rd__field">
              <div className="sso-rd__role-add">
                <Combobox
                  value={draft}
                  onChange={setDraft}
                  options={keys}
                  placeholder={t("Role name")}
                  ariaLabel={t("Role name")}
                  onSubmit={add}
                  isLoading={!areKeysLoaded}
                />
                <button
                  type="button"
                  className="sso-rd__role-add-btn"
                  onClick={add}
                  title={t("Add Permission")}
                  aria-label={t("Add Permission")}
                >
                  <IconPlus size={18} />
                </button>
              </div>
            </div>

            <div className="sso-rd__field">
              {isLoading ? (
                <Loading />
              ) : permissions.length === 0 ? (
                <div className="sso-rd__role-empty">{t("No permissions defined.")}</div>
              ) : (
                <div className="sso-rd__role-list">
                  {permissions.map((item) => (
                    <div className="sso-rd__role-row" key={item.permissionKey}>
                      <span className="sso-rd__role-key" title={item.permissionKey}>
                        {item.permissionKey}
                      </span>
                      <button
                        type="button"
                        className="sso-rd__role-del"
                        onClick={() => remove(item.permissionKey)}
                        title={t("Delete Permission")}
                        aria-label={t("Delete Permission")}
                      >
                        <IconTrash size={15} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </Modal>
      );
    }
  )
) as FC<Props>;

export default RoleAssignmentModal;
